import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { JwtPayload } from '../../domain/common.models';

declare global {
    interface Window {
        NREUM: any;
        newrelic: any;
    }
}

interface HttpErrorContext {
    url?: string;
    method?: string;
    status?: number;
    statusText?: string;
    timestamp?: number;
    userId?: string;
    companyId?: string;
    walletId?: string;
    phoneNumber?: string;
    userAgent?: string;
    sessionId?: string;
    requestBody?: any;
    responseBody?: any;
    duration?: number;
    environment?: string;
}

interface ErrorInfo {
    message: string;
    source?: string;
    lineno?: number;
    colno?: number;
    error?: Error;
    stack?: string;
    timestamp?: number;
    userAgent?: string;
    url?: string;
    userId?: string;
    sessionId?: string;
    buildVersion?: string;
    environment?: string;
    httpContext?: HttpErrorContext;
}

@Injectable({
    providedIn: 'root',
})
export class NewRelicAgentService {
    private userContext: JwtPayload | null = null;

    constructor() {
        this.initializeNewRelic();
    }

    /**
     * Set user context for session tracking
     * @param payload JWT payload containing user information
     */
    setUserContext(payload: JwtPayload): void {
        this.userContext = payload;

        if (!environment.send_new_relic) {
            console.log('New Relic user context set (development mode):', payload);
            return;
        }

        try {
            if (window.newrelic && payload.user_id) {
                window.newrelic.setUserId(payload.user_id);

                // Add custom attributes
                window.newrelic.addPageAction('user_context_set', {
                    companyId: payload.company_id,
                    walletId: payload.wallet_id,
                    phoneNumber: payload.phone_number,
                    timestamp: Date.now(),
                });

                console.log('✅ New Relic user context set successfully');
            }
        } catch (error) {
            console.warn('Failed to set New Relic user context:', error);
        }
    }

    /**
     * Enhanced HTTP error handling with rich context
     * @param error HTTP error response
     * @param duration Request duration in milliseconds
     * @param requestData Optional request data for context
     */
    handleHttpError(error: HttpErrorResponse, duration?: number, requestData?: any): void {
        const httpContext = this.buildHttpErrorContext(error, duration, requestData);

        if (!environment.send_new_relic) {
            this.logHttpErrorDevelopment(error, httpContext);
            return;
        }

        try {
            const errorInfo: ErrorInfo = {
                message: this.buildHttpErrorMessage(error),
                timestamp: Date.now(),
                userAgent: navigator.userAgent,
                url: window.location.href,
                userId: this.userContext?.user_id,
                environment: 'production',
                httpContext,
            };

            if (window.newrelic) {
                // Report HTTP error with context
                window.newrelic.noticeError(error, errorInfo);

                // Track as custom event for analytics
                window.newrelic.addPageAction('http_error', {
                    endpoint: httpContext.url,
                    method: httpContext.method,
                    status: httpContext.status,
                    statusText: httpContext.statusText,
                    duration: httpContext.duration,
                    userId: this.userContext?.user_id,
                    companyId: this.userContext?.company_id,
                    walletId: this.userContext?.wallet_id,
                    timestamp: Date.now(),
                });

                console.log('✅ HTTP error reported to New Relic:', {
                    url: httpContext.url,
                    status: httpContext.status,
                    method: httpContext.method,
                });
            } else {
                console.warn('New Relic not available, logging HTTP error locally:', errorInfo);
            }
        } catch (newRelicError) {
            console.error('Failed to report HTTP error to New Relic:', newRelicError);
            console.error('Original HTTP error:', error);
        }
    }

    /**
     * General error handling (for non-HTTP errors)
     */
    handleError(error: any): void {
        if (!environment.send_new_relic) {
            console.log('New Relic disabled in development');
            return;
        }

        try {
            const errorInfo: ErrorInfo = {
                message: error?.message || 'Unknown error',
                source: error?.filename || error?.source,
                lineno: error?.lineno,
                colno: error?.colno,
                error: error?.error || error,
                stack: error?.error?.stack || error?.stack,
                timestamp: Date.now(),
                userAgent: navigator.userAgent,
                url: window.location.href,
                userId: this.userContext?.user_id,
                buildVersion: 'unknown',
                environment: 'production',
            };

            if (window.newrelic) {
                window.newrelic.noticeError(error, errorInfo);
            } else {
                console.warn('New Relic not available, logging error locally:', errorInfo);
            }
        } catch (newRelicError) {
            console.error('Failed to report error to New Relic:', newRelicError);
            console.error('Original error:', error);
        }
    }

    /**
     * Track successful HTTP requests for performance monitoring
     */
    trackHttpSuccess(url: string, method: string, status: number, duration: number): void {
        if (!environment.send_new_relic) {
            console.log('HTTP Success tracked (development):', { url, method, status, duration });
            return;
        }

        try {
            if (window.newrelic) {
                window.newrelic.addPageAction('http_success', {
                    endpoint: url,
                    method,
                    status,
                    duration,
                    userId: this.userContext?.user_id,
                    timestamp: Date.now(),
                });
            }
        } catch (error) {
            console.warn('Failed to track HTTP success:', error);
        }
    }

    /**
     * Track business events (OTP flows, wallet operations)
     */
    trackBusinessEvent(eventName: string, attributes: Record<string, any> = {}): void {
        if (!environment.send_new_relic) {
            console.log('Business event tracked (development):', eventName, attributes);
            return;
        }

        try {
            if (window.newrelic) {
                window.newrelic.addPageAction(eventName, {
                    ...attributes,
                    userId: this.userContext?.user_id,
                    companyId: this.userContext?.company_id,
                    timestamp: Date.now(),
                });
            }
        } catch (error) {
            console.warn('Failed to track business event:', error);
        }
    }

    private buildHttpErrorContext(error: HttpErrorResponse, duration?: number, requestData?: any): HttpErrorContext {
        return {
            url: error.url || 'unknown',
            // method: this.extractHttpMethod(error),
            status: error.status,
            statusText: error.statusText,
            timestamp: Date.now(),
            userId: this.userContext?.user_id,
            companyId: this.userContext?.company_id,
            walletId: this.userContext?.wallet_id,
            phoneNumber: this.userContext?.phone_number,
            userAgent: navigator.userAgent,
            requestBody: requestData,
            responseBody: error.error,
            duration,
            environment: 'production',
        };
    }

    private buildHttpErrorMessage(error: HttpErrorResponse): string {
        const baseMessage = `HTTP ${error.status} Error`;
        const url = error.url || 'unknown';
        // const method = this.extractHttpMethod(error);

        return `${baseMessage}: ${url} - ${error.statusText || 'Unknown error'}`;
    }

    private logHttpErrorDevelopment(error: HttpErrorResponse, context: HttpErrorContext): void {
        console.group('🚨 HTTP Error (New Relic disabled - Development Mode)');
        console.error('Error:', error);
        console.table({
            URL: context.url,
            Method: context.method,
            Status: context.status,
            'Status Text': context.statusText,
            'Duration (ms)': context.duration,
            'User ID': context.userId,
        });
        console.groupEnd();
    }

    private initializeNewRelic() {
        if (!environment.send_new_relic) {
            console.log('New Relic initialization skipped in development');
            return;
        }

        try {
            // Initialize New Relic if available
            if (window.newrelic) {
                console.log('New Relic initialized');
            }
        } catch (error) {
            console.error('Failed to initialize New Relic:', error);
        }
    }
}
